import { useState, useRef, useEffect } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { FiMenu, FiX, FiLogOut, FiChevronDown, FiSettings } from 'react-icons/fi'
import { ContextSwitcher } from './ContextSwitcher'
import { UserSwitcher } from './UserSwitcher'
import { ThemeSwitcher } from '../common'
import { Badge } from '../ui/badge'
import { cn } from '../../lib/utils'
import { useNavigationItems } from './navigation/useNavigationItems'
import type { NavItemData, NavSection } from './navigation/useNavigationItems'

// Top-level nav link (direct items like Home, Explore)
function TopNavLink({ to, icon: Icon, label }: NavItemData) {
  const location = useLocation()
  const isActive = location.pathname === to || location.pathname.startsWith(to + '/')

  return (
    <NavLink
      to={to}
      className={cn(
        'flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-[var(--radius-button)] transition-colors whitespace-nowrap',
        isActive
          ? 'bg-sidebar-active text-sidebar-text font-semibold'
          : 'text-sidebar-text/80 hover:bg-sidebar-active/50 hover:text-sidebar-text'
      )}
      style={{ textDecoration: 'none' }}
    >
      <Icon size={16} />
      <span>{label}</span>
    </NavLink>
  )
}

// Dropdown menu for grouped sections
function NavDropdown({ section }: { section: NavSection }) {
  const [isOpen, setIsOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const location = useLocation()

  // Close on route change
  useEffect(() => { setIsOpen(false) }, [location.pathname])

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setIsOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [isOpen])

  const allItems = section.subSections
    ? section.subSections.flatMap(s => s.items)
    : section.items
  const hasActiveChild = allItems.some(
    item => location.pathname === item.to || location.pathname.startsWith(item.to + '/')
  )

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'flex items-center gap-1 px-3 py-1.5 text-sm rounded-[var(--radius-button)] transition-colors cursor-pointer whitespace-nowrap',
          hasActiveChild
            ? 'bg-sidebar-active text-sidebar-text font-semibold'
            : 'text-sidebar-text/80 hover:bg-sidebar-active/50 hover:text-sidebar-text'
        )}
      >
        <span>{section.title}</span>
        <FiChevronDown size={14} className={cn('transition-transform', isOpen && 'rotate-180')} />
      </button>

      {isOpen && (
        <div
          className="absolute top-full left-0 mt-1 min-w-[200px] rounded-[var(--radius-card)] shadow-lg border border-border/50 py-1 z-50"
          style={{ backgroundColor: 'var(--sidebar-bg)' }}
        >
          {section.subSections ? (
            section.subSections.map((sub) => (
              <div key={sub.title}>
                <p className="px-3 pt-2 pb-1 text-xs font-semibold text-sidebar-muted uppercase tracking-wide">
                  {sub.title}
                </p>
                {sub.items.length > 0 ? (
                  sub.items.map((item) => <DropdownItem key={item.to} {...item} />)
                ) : sub.emptyMessage ? (
                  <p className="px-3 py-1.5 text-xs text-sidebar-muted italic">{sub.emptyMessage}</p>
                ) : null}
              </div>
            ))
          ) : (
            section.items.map((item) => <DropdownItem key={item.to} {...item} />)
          )}
          {section.overflowLink && <DropdownItem {...section.overflowLink} />}
        </div>
      )}
    </div>
  )
}

function DropdownItem({ to, icon: Icon, label }: NavItemData) {
  const location = useLocation()
  const isActive = location.pathname === to || location.pathname.startsWith(to + '/')

  return (
    <NavLink
      to={to}
      className={cn(
        'flex items-center gap-2.5 px-3 py-2 text-sm transition-colors',
        isActive
          ? 'text-sidebar-text font-semibold bg-sidebar-active'
          : 'text-sidebar-text/80 hover:bg-sidebar-active/50 hover:text-sidebar-text'
      )}
      style={{ textDecoration: 'none' }}
    >
      <Icon size={15} />
      <span>{label}</span>
    </NavLink>
  )
}

// User menu dropdown (avatar + dropdown with profile, theme switcher, logout)
function UserMenu({ userName, userInitials, roleLabel, onLogout, hasSchool }: {
  userName: string; userInitials: string; roleLabel: string
  onLogout?: () => void; hasSchool: boolean
}) {
  const [isOpen, setIsOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isOpen) return
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setIsOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [isOpen])

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-2 py-1 rounded-[var(--radius-button)] hover:bg-sidebar-active/50 transition-colors cursor-pointer"
      >
        <div className="w-8 h-8 rounded-full bg-accent flex items-center justify-center">
          <span className="text-xs font-bold text-white">{userInitials}</span>
        </div>
        <FiChevronDown size={14} className={cn('text-sidebar-text/70 transition-transform', isOpen && 'rotate-180')} />
      </button>

      {isOpen && (
        <div
          className="absolute top-full right-0 mt-1 w-64 rounded-[var(--radius-card)] shadow-lg border border-border/50 py-2 z-50"
          style={{ backgroundColor: 'var(--sidebar-bg)' }}
        >
          {/* User info */}
          <div className="px-3 pb-2 border-b border-sidebar-muted/20 mb-1">
            <p className="text-sm font-semibold text-sidebar-text">{userName}</p>
            {roleLabel && (
              <Badge variant="outline" size="sm" className="bg-blue-500/20 text-blue-200 border-blue-400/30 text-[9px] mt-1">
                {roleLabel}
              </Badge>
            )}
          </div>

          {/* Context Switcher */}
          {hasSchool && (
            <div className="px-2 py-1 border-b border-sidebar-muted/20 mb-1">
              <ContextSwitcher />
            </div>
          )}

          {/* Dev: User Switcher */}
          <div className="px-2 py-1 border-b border-sidebar-muted/20 mb-1">
            <UserSwitcher dropUp={false} />
          </div>

          {/* Theme Switcher */}
          <div className="px-2 py-1 border-b border-sidebar-muted/20 mb-1">
            <ThemeSwitcher dropUp={false} />
          </div>

          {/* Account Settings */}
          <NavLink
            to="/account"
            className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-sidebar-text/80 hover:bg-sidebar-active/50 hover:text-sidebar-text transition-colors"
            style={{ textDecoration: 'none' }}
            onClick={() => setIsOpen(false)}
          >
            <FiSettings size={15} />
            <span>Account Settings</span>
          </NavLink>

          {/* Logout */}
          <button
            onClick={onLogout}
            className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-sidebar-text/80 hover:bg-sidebar-active/50 hover:text-sidebar-text transition-colors cursor-pointer"
          >
            <FiLogOut size={15} />
            <span>Logout</span>
          </button>
        </div>
      )}
    </div>
  )
}

interface TopNavProps {
  userName?: string
  userInitials?: string
  onLogout?: () => void
  isMobileMenuOpen: boolean
  onToggleMobileMenu: () => void
}

export function TopNav({
  userName = 'User',
  userInitials = 'U',
  onLogout,
  isMobileMenuOpen,
  onToggleMobileMenu,
}: TopNavProps) {
  const { sections, roleLabel, hasSchool } = useNavigationItems()

  // Split: first section (General) items are top-level links, rest are dropdowns
  const generalSection = sections.find(s => s.id === 'general')
  const dropdownSections = sections.filter(s => s.id !== 'general')

  return (
    <>
      <nav
        data-figma-component="TopNav"
        className="fixed top-0 left-0 right-0 z-[1000] flex items-center px-4 gap-1"
        style={{
          height: 'var(--theme-topnav-height, 64px)',
          backgroundColor: 'var(--sidebar-bg)',
          color: 'var(--sidebar-text)',
          borderBottom: `var(--theme-border-width, 1px) solid var(--sidebar-active-bg)`,
        }}
      >
        {/* Mobile hamburger */}
        <button
          className="flex lg:hidden items-center justify-center w-9 h-9 rounded-[var(--radius-button)] hover:bg-sidebar-active/50 transition-colors cursor-pointer mr-1"
          onClick={onToggleMobileMenu}
        >
          {isMobileMenuOpen ? <FiX size={20} /> : <FiMenu size={20} />}
        </button>

        {/* Logo */}
        <div className="flex items-center mr-4">
          <img src="/docurious-logo.png" alt="DOcurious" className="h-7 w-auto" />
        </div>

        {/* Desktop nav items */}
        <div className="hidden lg:flex items-center gap-0.5 flex-1 flex-wrap">
          {/* General items as direct links */}
          {generalSection?.items.map((item) => (
            <TopNavLink key={item.to} {...item} />
          ))}

          {/* Separator */}
          {dropdownSections.length > 0 && (
            <div className="w-px h-5 bg-sidebar-muted/30 mx-2" />
          )}

          {/* Other sections as dropdowns */}
          {dropdownSections.map((section) => (
            <NavDropdown key={section.id} section={section} />
          ))}
        </div>

        {/* Right side: user menu */}
        <div className="ml-auto flex items-center gap-2">
          <UserMenu
            userName={userName}
            userInitials={userInitials}
            roleLabel={roleLabel}
            onLogout={onLogout}
            hasSchool={hasSchool}
          />
        </div>
      </nav>

      {/* Mobile menu overlay */}
      {isMobileMenuOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/60 z-[998] lg:hidden"
            onClick={onToggleMobileMenu}
          />
          <div
            className="fixed top-0 left-0 right-0 z-[999] lg:hidden overflow-y-auto"
            style={{
              backgroundColor: 'var(--sidebar-bg)',
              paddingTop: 'var(--theme-topnav-height, 64px)',
              maxHeight: '85vh',
            }}
          >
            <div className="py-2">
              {sections.map((section) => (
                <div key={section.id} className="py-1">
                  <p className="px-4 py-1.5 text-xs font-bold text-sidebar-muted uppercase tracking-wide">
                    {section.title}
                  </p>
                  {section.subSections ? (
                    section.subSections.flatMap(sub => sub.items).map((item) => (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-sidebar-text/80 hover:bg-sidebar-active/50"
                        style={{ textDecoration: 'none' }}
                        onClick={onToggleMobileMenu}
                      >
                        <item.icon size={16} />
                        <span>{item.label}</span>
                      </NavLink>
                    ))
                  ) : (
                    section.items.map((item) => (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-sidebar-text/80 hover:bg-sidebar-active/50"
                        style={{ textDecoration: 'none' }}
                        onClick={onToggleMobileMenu}
                      >
                        <item.icon size={16} />
                        <span>{item.label}</span>
                      </NavLink>
                    ))
                  )}
                </div>
              ))}

              {/* Mobile bottom controls */}
              <div className="border-t border-sidebar-muted/20 mt-2 pt-2 px-3">
                {hasSchool && <div className="mb-2"><ContextSwitcher /></div>}
                <div className="mb-2"><UserSwitcher /></div>
                <div className="mb-2"><ThemeSwitcher /></div>
                <button
                  onClick={onLogout}
                  className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-sidebar-text/80 hover:bg-sidebar-active/50 cursor-pointer"
                >
                  <FiLogOut size={16} />
                  <span>Logout</span>
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  )
}
